import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MagnifyingGlass, Archive, ArrowCounterClockwise, Trash, FlagBannerFold, Microphone } from "@phosphor-icons/react";
import { AdminHeader, LiveIndicator } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { OutcomePicker } from "@/components/admin/OutcomePicker";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "outstanding", label: "Outstanding" },
  { key: "flagged", label: "Flagged" },
  { key: "low", label: "Low confidence" },
  { key: "voice", label: "Voice chats" },
  { key: "archived", label: "Archived" },
];

export default function Conversations() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [params] = useSearchParams();
  const [filter, setFilter] = useState(() => (FILTERS.some((f) => f.key === params.get("filter")) ? params.get("filter") : "all"));
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(
    (silent = false) => {
      if (!silent) setLoading(true);
      const params = {};
      if (q) params.search = q;
      if (filter === "flagged") params.flagged = true;
      if (filter === "low") params.low = true;
      if (filter === "archived") params.archived = true;
      if (filter === "voice") params.voice = true;
      if (filter === "outstanding") params.outcome = "outstanding";
      adminApi
        .get("/admin/conversations", { params })
        .then(({ data }) => setRows(data))
        .catch(() => {
          if (!silent) setRows([]);
        })
        .finally(() => {
          if (!silent) setLoading(false);
        });
    },
    [q, filter]
  );

  useEffect(() => {
    const t = setTimeout(() => load(), 250);
    return () => clearTimeout(t);
  }, [load]);

  // silent live refresh for the always-on admin view (no loading flicker)
  usePolling(() => load(true), 15000);

  const removeRow = (id) => setRows((prev) => prev.filter((c) => c.id !== id));

  const applyOutcome = (c, d) => {
    if (filter === "outstanding" && d.outcome !== "outstanding") removeRow(c.id);
    else setRows((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...d } : x)));
  };

  const unflag = async (c) => {
    try {
      await adminApi.put(`/admin/conversations/${c.id}`, { flagged: false });
      toast.success("Flag cleared");
      if (filter === "flagged") removeRow(c.id);
      else setRows((prev) => prev.map((x) => (x.id === c.id ? { ...x, flagged: false } : x)));
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const setArchived = async (c, archived) => {
    try {
      await adminApi.put(`/admin/conversations/${c.id}`, { archived });
      toast.success(archived ? "Conversation archived" : "Conversation restored");
      removeRow(c.id);
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const del = async (c) => {
    if (!window.confirm("Delete this conversation permanently? This cannot be undone.")) return;
    try {
      await adminApi.delete(`/admin/conversations/${c.id}`);
      toast.success("Conversation deleted");
      removeRow(c.id);
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const isArchived = filter === "archived";

  return (
    <div>
      <AdminHeader
        title="Conversations"
        subtitle="Review customer chats — clear flags, archive test chats, or delete them"
        actions={<LiveIndicator />}
      />
      <div className="p-8">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-2 bg-[#0F0F0F] border border-neutral-800 rounded-full px-4 py-2.5 w-80 focus-within:border-[#73bf44] transition-colors">
            <MagnifyingGlass size={16} className="text-neutral-500" />
            <input
              data-testid="conversations-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by chassis, model, keyword (e.g. PM335)..."
              className="bg-transparent outline-none text-sm text-white w-full placeholder:text-neutral-600"
            />
          </div>
          <div className="flex items-center gap-1 bg-[#0F0F0F] border border-neutral-800 rounded-full p-1">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`conversations-filter-${f.key}`}
                className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                  filter === f.key ? "bg-[#73bf44] text-[#050505] font-semibold" : "text-neutral-400 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-neutral-600">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="text-neutral-600 border border-dashed border-neutral-800 rounded-xl py-16 text-center">
            No conversations found.
          </div>
        ) : (
          <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
                  <th className="px-5 py-3 font-medium">Last message</th>
                  <th className="px-5 py-3 font-medium">Caravan</th>
                  <th className="px-5 py-3 font-medium">Msgs</th>
                  <th className="px-5 py-3 font-medium">Outcome</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Updated</th>
                  <th className="px-5 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr
                    key={c.id}
                    data-testid="conversation-row"
                    onClick={() => navigate(`/admin/conversations/${c.id}`)}
                    className="border-b border-neutral-800/60 last:border-0 hover:bg-white/[0.02] cursor-pointer transition-colors"
                  >
                    <td className="px-5 py-3.5 max-w-xs">
                      <div className="text-neutral-200 truncate">{c.last_message || "New conversation"}</div>
                      {c.customer_name && <div className="text-xs text-neutral-600">{c.customer_name}</div>}
                    </td>
                    <td className="px-5 py-3.5">
                      {c.chassis_number ? (
                        <div>
                          <div className="text-neutral-200">{c.model || "—"}</div>
                          <div className="font-mono text-xs text-[#73bf44]">{c.chassis_number}</div>
                        </div>
                      ) : (
                        <span className="text-neutral-600">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-neutral-400">{c.message_count || 0}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex flex-col items-start gap-1">
                        <OutcomePicker conv={c} onChange={(d) => applyOutcome(c, d)} />
                        {c.ticket_ref && <span className="font-mono text-[0.6rem] text-sky-300" data-testid="conversation-ticket-ref">{c.ticket_ref}</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex gap-1.5">
                        {c.has_voice && (
                          <span className="inline-flex items-center gap-1 text-[0.6rem] text-[#73bf44] border border-[#73bf44]/40 rounded-full px-2 py-0.5" data-testid="conversation-voice-badge">
                            <Microphone size={10} weight="fill" /> voice
                          </span>
                        )}
                        {c.flagged && <span className="text-[0.6rem] text-red-400 border border-red-500/30 rounded-full px-2 py-0.5">flagged</span>}
                        {c.low_confidence && <span className="text-[0.6rem] text-amber-400 border border-amber-500/30 rounded-full px-2 py-0.5">low</span>}
                        {!c.flagged && !c.low_confidence && !c.has_voice && <span className="text-neutral-700 text-xs">ok</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-neutral-500 text-xs">{fmt(c.updated_at)}</td>
                    <td className="px-5 py-3.5" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {c.flagged && (
                          <button
                            onClick={() => unflag(c)}
                            data-testid="conversation-unflag"
                            title="Clear flag"
                            className="p-1.5 rounded-lg text-neutral-400 hover:text-[#73bf44] hover:bg-[#73bf44]/10 transition-colors"
                          >
                            <FlagBannerFold size={16} />
                          </button>
                        )}
                        {isArchived ? (
                          <button
                            onClick={() => setArchived(c, false)}
                            data-testid="conversation-restore"
                            title="Restore"
                            className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-white/[0.06] transition-colors"
                          >
                            <ArrowCounterClockwise size={16} />
                          </button>
                        ) : (
                          <button
                            onClick={() => setArchived(c, true)}
                            data-testid="conversation-archive"
                            title="Archive (hide from dashboard)"
                            className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-white/[0.06] transition-colors"
                          >
                            <Archive size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => del(c)}
                          data-testid="conversation-delete"
                          title="Delete permanently"
                          className="p-1.5 rounded-lg text-neutral-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function fmt(iso) {
  try {
    return new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}
